import { Component, EventEmitter, Inject, Input, LOCALE_ID, OnInit, Output } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { debounceTime, pairwise, startWith } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { ContentState } from '../../../store/state/content.state';
import { DatePipe } from '@angular/common';

const DATE_VALIDATOR = Validators.pattern(/([2]\d{3}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01]))/);
const EMPTY_RANGE = { start: null, end: null };

interface DateRange {
    start: string;
    end: string;
}

@UntilDestroy()
@Component({
    selector: 'u4-date-filter',
    templateUrl: './date-filter.component.html',
    styles: []
})
export class DateFilterComponent implements OnInit {

    @Output() filtered: EventEmitter<DateRange | null> = new EventEmitter<DateRange | null>();
    @Input() startDate: string;
    @Input() endDate: string;

    public form: UntypedFormGroup;
    public labels: { [key: string]: string };
    public active = false;
    public options = [
        { key: 'year', days: 364 },
        { key: 'month', days: 30 },
        { key: 'week', days: 7 }
    ];

    private datePipe: DatePipe;

    constructor(
        private store: Store<ContentState>,
        @Inject(LOCALE_ID) private locale: string
    ) { }

    get label() {
        if (this.startDate && this.endDate) {
            return `${this.datePipe.transform(this.startDate, 'mediumDate')} - ${this.datePipe.transform(this.endDate, 'mediumDate')}`;
        }

        return this.labels['faceted-search.label.any-date'];
    }

    ngOnInit(): void {
        this.datePipe = new DatePipe(this.locale);

        this.form = new UntypedFormGroup({
            start: new UntypedFormControl(this.startDate || null, [DATE_VALIDATOR]),
            end: new UntypedFormControl(this.endDate || null, [DATE_VALIDATOR]),
        });

        if (this.startDate && this.endDate) {
            this.active = true;
        }

        this.store.select(state => state.labels).pipe(
            untilDestroyed(this)
        ).subscribe(labels => this.labels = labels);

        this.form.valueChanges
            .pipe(untilDestroyed(this), debounceTime(200), startWith(null as string), pairwise())
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            .subscribe(this.filter.bind(this));
    }

    filter([prevRange, range]: [DateRange, DateRange]) {
        if (!this.form.valid || !range.start || !range.end) {
            this.active = false;
            return;
        }

        if (range.start > range.end) {
            // reset to previous range
            this.form.patchValue(prevRange || EMPTY_RANGE, { emitEvent: false });
        } else {
            // apply filter
            this.filtered.emit(range);
            this.active = true;
        }
    }

    filterBy(days: number) {
        const start = new Date();
        const end = new Date();
        start.setDate(start.getDate() - days);

        this.form.patchValue({
            start: start.toISOString().split('T')[0],
            end: end.toISOString().split('T')[0]
        });
    }

    reset() {
        this.form.patchValue(EMPTY_RANGE);
        this.filtered.emit(EMPTY_RANGE);
    }

}
